import type { ComponentType } from 'react';
import { IconHome, IconPatients } from '@adc/parallax-icons';

import { DOMAINS } from '~/features/navigation/domains';
import ArchiveIcon from '~/icons/archive.svg?react';
import KeyboardIcon from '~/icons/keyboard.svg?react';
import type { FileRouteTypes } from '~/routeTree.gen';
import { buildTestId } from '~/utils/buildTestId';

const APP_MENU_TEST_ID = 'app-menu-item';

export const APP_MENU_ITEMS = {
  CustomerAdministration: {
    id: DOMAINS.CUSTOMER_ADMINISTRATION,
    label: 'Customer Administration',
    testID: buildTestId({
      testID: APP_MENU_TEST_ID,
      suffix: ['customer', 'administration'],
    }),
  },
  AbbottAdministration: {
    id: DOMAINS.ABBOTT_ADMINISTRATION,
    label: 'Abbott Administration',
    testID: buildTestId({
      testID: APP_MENU_TEST_ID,
      suffix: ['abbott', 'administration'],
    }),
  },
  Education: {
    id: DOMAINS.EDUCATION,
    label: 'Education',
    testID: buildTestId({
      testID: APP_MENU_TEST_ID,
      suffix: 'education',
    }),
  },
  Outpatient: {
    id: DOMAINS.OUTPATIENT,
    label: 'Patients',
    testID: buildTestId({
      testID: APP_MENU_TEST_ID,
      suffix: 'outpatient',
    }),
  },
  Administration: {
    id: DOMAINS.USER_ADMINISTRATION,
    label: 'Administration',
    testID: buildTestId({
      testID: APP_MENU_TEST_ID,
      suffix: 'administration',
    }),
  },
} as const;

const NAVIGATION_TEST_ID = 'navigation-item';

type ItemType = {
  id: string;
  label: string;
  path: FileRouteTypes['to'];
  icon: ComponentType;
  testID: string;
};

export const NAVIGATION_MENU_ITEMS = {
  AbbottUserAdministration: {
    id: 'abbott-user-administration',
    label: 'Users',
    icon: IconPatients,
    path: '/abbott-administration/users',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: ['abbott', 'administration'],
    }),
  } satisfies ItemType,
  AbbottAdminArchive: {
    id: 'archive',
    label: 'Archive',
    icon: ArchiveIcon,
    path: '/abbott-administration/archive',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: ['abbott', 'administration', 'archive'],
    }),
  } satisfies ItemType,
  UserAdministration: {
    id: 'user-administration',
    label: 'User Administration',
    icon: IconPatients,
    path: '/user-administration/users',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: ['user', 'administration'],
    }),
  } satisfies ItemType,
  CustomerUserAdministration: {
    id: 'user-administration',
    label: 'Users',
    icon: IconPatients,
    path: '/customer-administration/users',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: ['user', 'administration'],
    }),
  } satisfies ItemType,
  PatientAdministration: {
    id: 'patients',
    label: 'Patients',
    icon: IconHome,
    path: '/outpatient/patients',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'patients',
    }),
  } satisfies ItemType,
  PatientAdministrationArchive: {
    id: 'archive',
    label: 'Archive',
    icon: ArchiveIcon,
    path: '/outpatient/archive',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: ['outpatient', 'archive'],
    }),
  } satisfies ItemType,
  PatientsCustomerAdministration: {
    id: 'patients',
    label: 'Patients',
    icon: IconPatients,
    path: '/customer-administration/patients',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'patients',
    }),
  } satisfies ItemType,
  Departments: {
    id: 'departments',
    label: 'Departments',
    icon: ArchiveIcon,
    path: '/customer-administration/departments',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'departments',
    }),
  } satisfies ItemType,
  DepartmentsAndSettings: {
    id: 'departments',
    label: 'Departments & Settings',
    icon: ArchiveIcon,
    path: '/user-administration/departments',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'departments',
    }),
  } satisfies ItemType,
  Archive: {
    id: 'archive',
    label: 'Archive',
    icon: ArchiveIcon,
    path: '/user-administration/archive',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'archive',
    }),
  } satisfies ItemType,
  IDNS: {
    id: 'idns',
    label: 'IDNs',
    icon: KeyboardIcon,
    path: '/customer-administration/idns',
    testID: buildTestId({
      testID: NAVIGATION_TEST_ID,
      suffix: 'idns',
    }),
  } satisfies ItemType,
} as const;
